import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Play, 
  FileText, 
  Headphones, 
  ArrowLeft,
  Volume2,
  X,
  Heart,
  HeartOff
} from "lucide-react";
import { toast } from "sonner";

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingItem, setPlayingItem] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchFavorites();
    } else {
      navigate("/login");
    }
  }, [user]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/content-favorites`);
      setFavorites(response.data);
    } catch (error) {
      console.error("Failed to fetch favorites", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = async (item) => {
    const content = item.content;
    if (!content) return;

    if (playingItem?.content_id === content.content_id) {
      if (audioRef.current?.paused) {
        audioRef.current.play();
      } else {
        audioRef.current?.pause();
      }
      return;
    }

    try {
      const response = await axios.get(`${API}/content/stream/${content.content_id}`);
      setPlayingItem({ ...content, streamUrl: response.data.stream_url });
    } catch (error) {
      toast.error("Failed to play content");
    }
  };

  const removeFavorite = async (contentId) => {
    try {
      await axios.delete(`${API}/content-favorites/${contentId}`);
      setFavorites(prev => prev.filter(f => f.content_id !== contentId));
      toast.success("Removed from favorites");
    } catch (error) {
      toast.error("Failed to remove favorite");
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading favorites...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="favorites-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            className="mb-4 text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/library")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Library
          </Button>
          <h1 className="font-serif text-3xl font-bold mb-2 flex items-center gap-3">
            <Heart className="h-8 w-8 text-red-500 fill-current" />
            My Favorites
          </h1>
          <p className="text-muted-foreground">
            {favorites.length} {favorites.length === 1 ? 'item' : 'items'} saved
          </p>
        </div>

        {/* Favorites List */}
        {favorites.length === 0 ? (
          <Card className="p-12 text-center">
            <HeartOff className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-serif text-xl mb-2">No favorites yet</h3>
            <p className="text-muted-foreground mb-4">
              Start adding content to your favorites from the library
            </p>
            <Button onClick={() => navigate("/library")} className="bg-gold text-primary-foreground hover:bg-gold/90">
              Browse Library
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {favorites.map(fav => {
              const item = fav.content;
              if (!item) return null;
              
              return (
                <Card 
                  key={fav.favorite_id}
                  className="group hover:border-gold/50 transition-all"
                  data-testid={`favorite-item-${item.content_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {/* Thumbnail */}
                      <div className="w-20 h-20 bg-card rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {item.thumbnail_url ? (
                          <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : item.content_type === "audio" ? (
                          <Headphones className="h-8 w-8 text-gold" />
                        ) : (
                          <FileText className="h-8 w-8 text-gold" />
                        )}
                      </div>

                      {/* Content Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-medium group-hover:text-gold transition-colors">
                              {item.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">{item.course_name} • {item.category}</p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {item.content_type === "audio" ? "Audio" : "PDF"}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                          {item.description}
                        </p>
                        
                        <div className="flex items-center justify-between mt-4">
                          {item.duration && (
                            <span className="text-sm text-muted-foreground">
                              {formatDuration(item.duration)}
                            </span>
                          )}
                          
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                              onClick={() => removeFavorite(item.content_id)}
                            >
                              <Heart className="h-4 w-4 fill-current mr-1" />
                              Remove
                            </Button>
                            
                            {item.content_type === "audio" ? (
                              <Button
                                size="sm"
                                variant={playingItem?.content_id === item.content_id ? "default" : "outline"}
                                className={playingItem?.content_id === item.content_id ? "bg-gold text-primary-foreground" : ""}
                                onClick={() => handlePlay(fav)}
                              >
                                {playingItem?.content_id === item.content_id ? (
                                  <>
                                    <Volume2 className="h-4 w-4 mr-1" />
                                    Playing
                                  </>
                                ) : (
                                  <>
                                    <Play className="h-4 w-4 mr-1" />
                                    Play
                                  </>
                                )}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(item.file_url, "_blank")}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                View PDF
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Audio Player */}
      {playingItem && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 z-50" data-testid="audio-player">
          <div className="container mx-auto flex items-center gap-4">
            <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Headphones className="h-6 w-6 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm line-clamp-1">{playingItem.title}</h4>
              <p className="text-xs text-muted-foreground">{playingItem.course_name}</p>
            </div>
            <audio
              ref={audioRef}
              src={playingItem.streamUrl}
              autoPlay
              controls
              className="w-64 md:w-96"
              onEnded={() => setPlayingItem(null)}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                audioRef.current?.pause();
                setPlayingItem(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
